// Define all the enums and types needed for empty floor tiles.
// Also define any methods needed to generate and manipulate this data.

import Tile from 'app/src/engine/tiles/Tile';
import getRandomElementOfEnum from 'app/src/utils/getRandomElementOfEnum';

export const tileName = 'EmptyFloorTile';

export enum EmptyFloorTileTypeError {
	InvalidType,
}

export enum EmptyFloorTileType {
	Type1,
	Type2,
	Type3,
	Type4,
}

export function getEmptyFloorTileType(name: string): EmptyFloorTileType {
	switch (name) {
		case 'Type1':
			return EmptyFloorTileType.Type1;
		case 'Type2':
			return EmptyFloorTileType.Type2;
		case 'Type3':
			return EmptyFloorTileType.Type3;
		case 'Type4':
			return EmptyFloorTileType.Type4;
		default:
			throw EmptyFloorTileTypeError.InvalidType;
	}
}

export enum EmptyFloorTileOrientationError {
	InvalidOrientation,
}

export enum EmptyFloorTileOrientation {
	Up,
	Down,
	Left,
	Right,
}

export function getEmptyFloorTileOrientation(name: string): EmptyFloorTileOrientation {
	switch (name) {
		case 'Up':
			return EmptyFloorTileOrientation.Up;
		case 'Right':
			return EmptyFloorTileOrientation.Right;
		case 'Down':
			return EmptyFloorTileOrientation.Down;
		case 'Left':
			return EmptyFloorTileOrientation.Left;
		default:
			throw EmptyFloorTileOrientationError.InvalidOrientation;
	}
}

export default class EmptyFloorTile extends Tile {
	get tileName(): string {
		return tileName;
	}

	get type(): EmptyFloorTileType {
		return this._type;
	}
	get orientation(): EmptyFloorTileOrientation {
		return this._orientation;
	}

	private _type: EmptyFloorTileType;
	private _orientation: EmptyFloorTileOrientation;

	constructor(type: EmptyFloorTileType, orientation: EmptyFloorTileOrientation) {
		super();

		// Save params.
		this._type = type;
		this._orientation = orientation;
	}

	public isEqualTo(tile: EmptyFloorTile): boolean {
		return tile.type === this.type && tile.orientation === this.orientation;
	}

	public clone(): EmptyFloorTile {
		const clone = new EmptyFloorTile(this.type, this.orientation);
		clone.walls = new Set(this.walls);
		clone.allowsMultipleMovableItems = this.allowsMultipleMovableItems;
		return clone;
	}
}

export function generateRandomEmptyFloorTile(): EmptyFloorTile {
	return new EmptyFloorTile(
		getRandomElementOfEnum<EmptyFloorTileType>(EmptyFloorTileType),
		getRandomElementOfEnum<EmptyFloorTileOrientation>(EmptyFloorTileOrientation),
	);
}

export function rotateEmptyFloorTile(tile: EmptyFloorTile, clockwise: boolean): EmptyFloorTile {
	let type = tile.type;
	let orientation = tile.orientation;

	if (clockwise) {
		let moveToNextType = false;

		switch (orientation) {
			case EmptyFloorTileOrientation.Up:
				orientation = EmptyFloorTileOrientation.Right;
				break;
			case EmptyFloorTileOrientation.Right:
				orientation = EmptyFloorTileOrientation.Down;
				break;
			case EmptyFloorTileOrientation.Down:
				orientation = EmptyFloorTileOrientation.Left;
				break;
			case EmptyFloorTileOrientation.Left:
				orientation = EmptyFloorTileOrientation.Up;
				moveToNextType = true;
				break;
		}

		if (moveToNextType) {
			switch (type) {
				case EmptyFloorTileType.Type1:
					type = EmptyFloorTileType.Type2;
					break;
				case EmptyFloorTileType.Type2:
					type = EmptyFloorTileType.Type3;
					break;
				case EmptyFloorTileType.Type3:
					type = EmptyFloorTileType.Type4;
					break;
				case EmptyFloorTileType.Type4:
					type = EmptyFloorTileType.Type1;
					break;
			}
		}
	} else {
		let moveToPreviousType = false;

		switch (orientation) {
			case EmptyFloorTileOrientation.Up:
				orientation = EmptyFloorTileOrientation.Left;
				moveToPreviousType = true;
				break;
			case EmptyFloorTileOrientation.Right:
				orientation = EmptyFloorTileOrientation.Up;
				break;
			case EmptyFloorTileOrientation.Down:
				orientation = EmptyFloorTileOrientation.Right;
				break;
			case EmptyFloorTileOrientation.Left:
				orientation = EmptyFloorTileOrientation.Down;
				break;
		}

		if (moveToPreviousType) {
			switch (type) {
				case EmptyFloorTileType.Type1:
					type = EmptyFloorTileType.Type4;
					break;
				case EmptyFloorTileType.Type2:
					type = EmptyFloorTileType.Type1;
					break;
				case EmptyFloorTileType.Type3:
					type = EmptyFloorTileType.Type2;
					break;
				case EmptyFloorTileType.Type4:
					type = EmptyFloorTileType.Type3;
					break;
			}
		}
	}

	return new EmptyFloorTile(type, orientation);
}
